var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

import React from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { createYScale } from '../../utils/scale-helpers';
import GriffPropTypes, { singleSeriePropType } from '../../utils/proptypes';
import AxisPlacement from '../AxisPlacement';
import ScalerContext from '../../context/Scaler';
import ZoomRect from '../ZoomRect';
import Axes from '../../utils/Axes';
var propTypes = {
  zoomable: PropTypes.bool,
  offsetx: PropTypes.number.isRequired,
  series: singleSeriePropType,
  collection: GriffPropTypes.collection,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  yAxisPlacement: GriffPropTypes.axisPlacement,
  // Number => String
  tickFormatter: PropTypes.func.isRequired,
  defaultColor: PropTypes.string,
  ticks: PropTypes.number,
  // These are populated by Griff.
  subDomainsByItemId: GriffPropTypes.subDomainsByItemId.isRequired
};
var defaultProps = {
  series: null,
  collection: null,
  zoomable: true,
  onMouseEnter: null,
  onMouseLeave: null,
  yAxisPlacement: AxisPlacement.RIGHT,
  defaultColor: '#000',
  ticks: 0
};

var getItem = function getItem(series, collection) {
  return series || collection;
};

var getLineProps = function getLineProps(_ref) {
  var strokeWidth = _ref.strokeWidth,
      tickSizeInner = _ref.tickSizeInner,
      width = _ref.width,
      yAxisPlacement = _ref.yAxisPlacement;

  switch (yAxisPlacement) {
    case AxisPlacement.LEFT:
      return {
        x1: width - strokeWidth,
        x2: width - strokeWidth - tickSizeInner,
        y1: strokeWidth / 2,
        y2: strokeWidth / 2
      };

    case AxisPlacement.BOTH:
      throw new Error('BOTH is not a valid option for YAxis -- please specify RIGHT or LEFT');

    case AxisPlacement.RIGHT:
    case AxisPlacement.UNSPECIFIED:
    default:
      return {
        x1: 0,
        x2: tickSizeInner,
        y1: strokeWidth / 2,
        y2: strokeWidth / 2
      };
  }
};

var getTextProps = function getTextProps(_ref2) {
  var strokeWidth = _ref2.strokeWidth,
      tickPadding = _ref2.tickPadding,
      tickSizeInner = _ref2.tickSizeInner,
      width = _ref2.width,
      yAxisPlacement = _ref2.yAxisPlacement;

  switch (yAxisPlacement) {
    case AxisPlacement.LEFT:
      return {
        x: Math.max(width - tickSizeInner, 0) - tickPadding,
        y: strokeWidth / 2
      };

    case AxisPlacement.BOTH:
      throw new Error('BOTH is not a valid option for YAxis -- please specify RIGHT or LEFT');

    case AxisPlacement.RIGHT:
    case AxisPlacement.UNSPECIFIED:
    default:
      return {
        x: Math.max(tickSizeInner, 0) + tickPadding,
        y: strokeWidth / 2
      };
  }
};

var getPathString = function getPathString(_ref3) {
  var range = _ref3.range,
      strokeWidth = _ref3.strokeWidth,
      tickSizeOuter = _ref3.tickSizeOuter,
      width = _ref3.width,
      yAxisPlacement = _ref3.yAxisPlacement;

  switch (yAxisPlacement) {
    case AxisPlacement.LEFT:
      return ['M' + (width - tickSizeOuter) + ',' + (range[0] - strokeWidth), 'H' + (width - strokeWidth), 'V' + range[1], 'H' + (width - tickSizeOuter)].join('');

    case AxisPlacement.BOTH:
      throw new Error('BOTH is not a valid option for YAxis -- please specify RIGHT or LEFT');

    case AxisPlacement.RIGHT:
    case AxisPlacement.UNSPECIFIED:
    default:
      return [// Move to this (x,y); start drawing
      'M' + tickSizeOuter + ',' + (range[0] - strokeWidth), // Draw a horizontal line half strokeWidth long
      'H' + strokeWidth / 2, // Draw a vertical line from bottom to top
      'V' + range[1], // Finish with another horizontal line
      'H' + tickSizeOuter].join('');
  }
};

var getTextAnchor = function getTextAnchor(_ref4) {
  var yAxisPlacement = _ref4.yAxisPlacement;

  switch (yAxisPlacement) {
    case AxisPlacement.LEFT:
      return 'end';

    case AxisPlacement.BOTH:
      throw new Error('BOTH is not a valid option for YAxis -- please specify RIGHT or LEFT');

    case AxisPlacement.RIGHT:
    case AxisPlacement.UNSPECIFIED:
    default:
      return 'start';
  }
};

var YAxis = function YAxis(_ref5) {
  var collection = _ref5.collection,
      defaultColor = _ref5.defaultColor,
      height = _ref5.height,
      offsetx = _ref5.offsetx,
      onMouseEnter = _ref5.onMouseEnter,
      onMouseLeave = _ref5.onMouseLeave,
      series = _ref5.series,
      subDomainsByItemId = _ref5.subDomainsByItemId,
      tickFormatter = _ref5.tickFormatter,
      ticks = _ref5.ticks,
      width = _ref5.width,
      yAxisPlacement = _ref5.yAxisPlacement,
      zoomable = _ref5.zoomable;
  var item = getItem(series, collection);
  var color = item.color || defaultColor;
  var scale = createYScale(Axes.y(subDomainsByItemId[item.id]), height);
  var axis = d3.axisRight(scale);
  var tickFontSize = 14;
  var strokeWidth = 2;
  var halfStrokeWidth = strokeWidth / 2;
  var tickSizeOuter = axis.tickSizeOuter();
  var tickSizeInner = axis.tickSizeInner();
  var tickPadding = axis.tickPadding(); // same as for xAxis but consider height of the screen ~two times smaller

  var nTicks = ticks || Math.floor(height / 50) || 1;
  var values = scale.ticks(nTicks);
  var range = scale.range().map(function (r) {
    return r + halfStrokeWidth;
  });
  var axisElement = React.createElement('g', {
    className: 'axis y-axis',
    fill: 'none',
    fontSize: tickFontSize,
    textAnchor: getTextAnchor({
      yAxisPlacement: yAxisPlacement
    }),
    strokeWidth: strokeWidth
  }, React.createElement('path', {
    stroke: color,
    d: getPathString({
      range: range,
      strokeWidth: strokeWidth,
      tickSizeOuter: tickSizeOuter,
      width: width,
      yAxisPlacement: yAxisPlacement
    })
  }), values.map(function (v) {
    var lineProps = _extends({
      stroke: color
    }, getLineProps({
      strokeWidth: strokeWidth,
      tickSizeInner: tickSizeInner,
      width: width,
      yAxisPlacement: yAxisPlacement
    }));

    var textProps = _extends({
      fill: color,
      dy: '0.32em'
    }, getTextProps({
      strokeWidth: strokeWidth,
      tickPadding: tickPadding,
      tickSizeInner: tickSizeInner,
      width: width,
      yAxisPlacement: yAxisPlacement
    }));

    return React.createElement('g', {
      key: +v,
      opacity: 1,
      transform: 'translate(0, ' + scale(v) + ')'
    }, React.createElement('line', lineProps), React.createElement('text', _extends({
      className: 'tick-value'
    }, textProps), tickFormatter(v, values)));
  }));
  var cursor = zoomable ? 'move' : 'inherit';
  return React.createElement('g', {
    className: 'axis-y',
    transform: 'translate(' + offsetx + ', 0)',
    cursor: cursor,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }, axisElement, zoomable && React.createElement(ZoomRect, {
    width: width,
    height: height,
    zoomAxes: {
      y: true
    },
    itemIds: [getItem(series, collection).id]
  }));
};

YAxis.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
YAxis.defaultProps = defaultProps;
export default (function (props) {
  return React.createElement(ScalerContext.Consumer, null, function (_ref6) {
    var subDomainsByItemId = _ref6.subDomainsByItemId;
    return React.createElement(YAxis, _extends({}, props, {
      subDomainsByItemId: subDomainsByItemId
    }));
  });
});